// ═══ ANALIZE TAB ══════════════════════════════════════════════════════════════
// Tab-ul de analize: liste de valori pe categorii, grafice de evoluție, interpretare
// AI, comparare imagistică și formularele de adăugare/editare valori.
import React, { useState } from "react";
import { fmAlert, fmConfirm } from './dialog';
import { t, aiIn, aiTextLang } from './i18n';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { fmtD, escapeHtml, todayISO, toText, asArr, foldDia } from './helpers';
import { ALL_ANALYTES } from './data';
import { DateField, HeartSpinner, SearchableSelect } from './ui';

// Categorii de analize (pentru gruparea valorilor în liste)
const LAB_CATS = {
  "Semne vitale":["tensiune sistolica","tensiune diastolica","puls","saturatie oxigen","temperatura","frecventa respiratorie","greutate","inaltime","imc"],
  "Hematologie":["hemoglobina","hematocrit","leucocite","trombocite","eritrocite","vsh","neutrofile","limfocite","monocite","eozinofile","bazofile","reticulocite","mcv","mch","mchc","rdw","mpv"],
  "Biochimie":["glicemie","glucoza","creatinina","uree","acid uric","bilirubina totala","bilirubina directa","bilirubina indirecta","proteine totale","albumina","fibrinogen","feritina","transferina","proteina c reactiva","pcr","procalcitonina"],
  "Enzime":["alt","tgp","ast","tgo","ggt","fosfataza alcalina","amilaza","lipaza","ldh","cpk","ck","troponina","bnp"],
  "Lipide":["colesterol total","colesterol ldl","colesterol hdl","trigliceride","apolipoprotein"],
  "Hormoni":["tsh","ft4","ft3","t4","t3","cortizol","insulina","hba1c","estradiol","progesteron","testosteron","prolactina","lh","fsh","parathormon","pth","vitamina d","calcitonina"],
  "Minerale":["fier seric","calciu","magneziu","potasiu","sodiu","fosfor","zinc","cupru","clor","bicarbonat"],
  "Coagulare":["inr","tp","aptt","fibrinogen","d-dimeri","timp protrombina"],
  "Urină":["creatinina urinara","proteinurie","glucozurie","ph urinar","densitate urinara","eritrocite urina","leucocite urina","nitriți","corpi cetonici"],
  "Imunologie":["iga","igg","igm","ige","ana","anca","factor reumatoid","anti-ccp","complement c3","complement c4"],
  "Altele":[]
};
const getCat = name => { const n=(name||"").toLowerCase(); for(const[c,ks] of Object.entries(LAB_CATS)) if(ks.some(k=>n.includes(k))) return c; return "Altele"; };

// Culoarea unui punct în funcție de status (peste=roșu, sub=galben, normal=verde)
const statusColor = s => s==="high" ? "#f87171" : s==="low" ? "#fbbf24" : "#34d399";
// Colorează fiecare SEGMENT al liniei după status: verde doar dacă ambele capete
// sunt normale; altfel culoarea capătului anormal (roșu = crescut prioritar).
// Întoarce perechi de stop-uri (câte 2 per segment) pentru un gradient cu muchii dure.
function segmentStops(points){
  const n = points.length;
  if(n < 2) return [];
  const worse = (a,b) => (a._status==="high"||b._status==="high") ? "#f87171"
    : (a._status==="low"||b._status==="low") ? "#fbbf24" : "#34d399";
  const stops = [];
  for(let k=0;k<n-1;k++){
    const c = worse(points[k], points[k+1]);
    stops.push({o:(k/(n-1))*100, c}, {o:((k+1)/(n-1))*100, c});
  }
  return stops;
}
// Punct colorat pentru graficele de analize (recharts primește props: cx, cy, payload)
const StatusDot = (props) => {
  const { cx, cy, payload, index, r=4 } = props;
  if(cx==null || cy==null) return null;
  return <circle key={index} cx={cx} cy={cy} r={r} fill={statusColor(payload?._status)} stroke="var(--surface)" strokeWidth={1}/>;
};

export function InterpretAnalysesForm({latest, analyteMap, callAI, patient}) {
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const ranRef = React.useRef(false);

  async function run() {
    setLoading(true); setResult(null); setError(null);
    // Trimite valorile curente + evoluția (istoric) fiecărei analize
    const data = latest.map(v=>{
      const hist = (analyteMap[v.name]||[]).slice(0,5).map(h=>({data:h.date,val:h.value,unit:h.unit,status:h.status}));
      return {analiza:v.name, valoare:v.value, unit:v.unit, interval:v.referenceRange, status:v.status, istoric:hist};
    });
    const prompt = `Ești un asistent medical AI. Interpretează aceste analize de laborator ale unui pacient ${aiIn()}.
Date pacient: diagnostice ${JSON.stringify(patient.conditions||[])}.
Analize (cu istoric recent): ${JSON.stringify(data)}

Oferă o interpretare clinică utilă:
- Grupează pe sisteme/organe unde e relevant.
- Evidențiază valorile anormale și ce pot indica.
- Analizează TENDINȚELE din istoric (în creștere/scădere/stabil).
- Corelează cu diagnosticele cunoscute.

Returnează JSON: {"overview":"privire generală 2-3 propoziții","abnormalFindings":["constatare despre o valoare anormală și semnificația ei"],"trends":["tendință observată în timp"],"recommendations":["recomandare generală"]}
${aiTextLang()} Fii echilibrat, nu alarmist. NU pune diagnostice ferme.`;
    const res = await callAI(prompt, 8000);
    setLoading(false);
    if(res && !res._error) setResult(res);
    else setError(window._lastAIError || t("Interpretarea a eșuat. Încearcă din nou."));
  }

  // Rulează o singură dată per deschidere; evită și dublarea din StrictMode/remontări.
  // Intenționat fără dependențe: `run` nu trebuie să re-declanșeze efectul.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(()=>{ if(ranRef.current) return; ranRef.current = true; run(); }, []);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{padding:"9px 12px",background:"var(--amber-bg-soft)",border:"1px solid var(--amber-line)",borderRadius:10,fontSize:11,color:"#c4a355"}}>
        ⓘ {t("Interpretare generată de AI, orientativă. Nu înlocuiește consultul și interpretarea unui medic.")}
      </div>
      {loading&&<HeartSpinner label={t("Se analizează valorile...")}/>}
      {error&&!loading&&(
        <div style={{padding:"14px",background:"var(--red-bg-soft)",border:"1px solid var(--red)",borderRadius:10,fontSize:13,color:"var(--text-2)",lineHeight:1.5}}>
          <div style={{marginBottom:10}}>⚠ {error}</div>
          <button className="bg" style={{fontSize:12}} onClick={()=>run()}>{t("↻ Încearcă din nou")}</button>
        </div>
      )}
      {result&&(
        <>
          {result.overview&&<div className="card" style={{border:"1px solid var(--blue-line)",margin:0}}><div className="lbl" style={{color:"var(--blue)"}}>{t("Privire generală")}</div><div style={{fontSize:13,lineHeight:1.6,marginTop:4}}>{toText(result.overview)}</div></div>}
          {asArr(result.abnormalFindings).length>0&&<div className="card" style={{margin:0,border:"1px solid var(--red-line)"}}><div className="lbl" style={{color:"var(--red)",marginBottom:6}}>{t("Valori de urmărit")}</div>{asArr(result.abnormalFindings).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:5,lineHeight:1.5}}>• {toText(x)}</div>)}</div>}
          {asArr(result.trends).length>0&&<div className="card" style={{margin:0}}><div className="lbl" style={{color:"var(--green)",marginBottom:6}}>{t("Tendințe")}</div>{asArr(result.trends).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:5,lineHeight:1.5}}>• {toText(x)}</div>)}</div>}
          {asArr(result.recommendations).length>0&&<div className="card" style={{margin:0,border:"1px solid var(--purple-line)"}}><div className="lbl" style={{color:"var(--purple)",marginBottom:6}}>{t("Recomandări")}</div>{asArr(result.recommendations).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:5,lineHeight:1.5}}>• {toText(x)}</div>)}</div>}
        </>
      )}
    </div>
  );
}

export function CompareImagingForm({imgList, callAI, savedCompare, onSaveCompare}) {
  // Selecție multiplă — implicit ultimele 2
  const [selected, setSelected] = React.useState(()=>imgList.slice(0,2).map(i=>i.docId));
  const [comparing, setComparing] = React.useState(false);
  const [result, setResult] = React.useState(savedCompare||null);

  function toggle(docId) {
    setSelected(p=>p.includes(docId)?p.filter(x=>x!==docId):[...p,docId]);
  }

  async function runCompare() {
    const studies = imgList.filter(x=>selected.includes(x.docId)).sort((a,b)=>a.date.localeCompare(b.date));
    if(studies.length<2) { fmAlert(t("Selectează cel puțin două studii.")); return; }
    // Filtrează studiile fără conținut extras — nu au ce compara
    const valid = studies.filter(s=>s.summary&&(s.summary.findings||s.summary.impression));
    if(valid.length<2) { fmAlert(t("Cel puțin două dintre studiile selectate nu au conținut extras. Reextrage-le din tab-ul Documente și încearcă din nou.")); return; }
    setComparing(true); setResult(null);
    // Trimite doar câmpurile relevante, compact — evită depășirea limitei
    const studiesText = valid.map((s,i)=>{
      const su=s.summary||{};
      return `STUDIU ${i+1} — ${fmtD(s.date)} (${su.studyType||"studiu"}${su.bodyPart?", "+su.bodyPart:""}):\nConstatări: ${su.findings||"-"}\nConcluzie: ${su.impression||"-"}`;
    }).join("\n\n");
    const prompt = `Ești un asistent medical. Compară cronologic aceste ${valid.length} studii imagistice ale aceluiași pacient și descrie evoluția în timp.
Studiile pot fi de tipuri diferite (CT, PET-CT, RMN, radiografie, ecografie) — ține cont de asta.

${studiesText}

Folosește limbaj SIMPLU, pe înțelesul pacientului (explică termenii medicali pe scurt).
Fii CONCIS — răspunsul trebuie să încapă într-un buget limitat de tokeni. Maxim 4 propoziții la "evolution", maxim 4 intrări scurte (o propoziție fiecare) la fiecare listă. Nu repeta informația din findings/impression, sintetizeaz-o.
Returnează DOAR acest JSON, fără alt text:
{"evolution":"descriere evoluție cronologică în max 4 propoziții","improvements":["ameliorare"],"worsenings":["agravare"],"stable":["aspect stabil"],"notes":"observații despre comparabilitate","recommendation":"recomandare"}
${aiTextLang()} Compară DOAR ce există în rapoarte, nu inventa.`;
    const res = await callAI(prompt, 8192);
    setComparing(false);
    if(res && !res._error) {
      const cmp = {...res, generatedAt:new Date().toLocaleString("ro-RO"), comparedDates:valid.map(s=>fmtD(s.date)), docIds:valid.map(s=>s.docId)};
      setResult(cmp);
      if(onSaveCompare) onSaveCompare(cmp);
    }
    else fmAlert(t("Comparația a eșuat: ") + (res?._error||window._lastAIError||t("eroare necunoscută")));
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:12,color:"var(--text-2)"}}>{t("Bifează studiile de comparat (minim 2). Pot fi de tipuri diferite (CT, PET-CT, RMN) — comparația se face cronologic, de la cel mai vechi la cel mai recent.")}</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:280,overflowY:"auto"}}>
        {[...imgList].sort((a,b)=>a.date.localeCompare(b.date)).map(img=>{
          const isSel=selected.includes(img.docId);
          const order=isSel?[...imgList].filter(x=>selected.includes(x.docId)).sort((a,b)=>a.date.localeCompare(b.date)).findIndex(x=>x.docId===img.docId)+1:null;
          return (
            <label key={img.docId} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 12px",background:isSel?"#12233a":"var(--surface)",border:`1px solid ${isSel?"#7c3aed":"var(--border-2)"}`,borderRadius:10,cursor:"pointer"}}>
              <input type="checkbox" checked={isSel} onChange={()=>toggle(img.docId)} style={{accentColor:"#7c3aed",width:16,height:16}}/>
              {order&&<span style={{background:"#7c3aed",color:"#fff",fontSize:10,fontWeight:700,borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{order}</span>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{img.docName}</div>
                <div style={{fontFamily:"monospace",fontSize:11,color:"var(--text-3)"}}>📅 {fmtD(img.date)}{img.summary?.studyType?` · ${img.summary.studyType}`:""}{img.summary?.bodyPart?` · ${img.summary.bodyPart}`:""}</div>
              </div>
            </label>
          );
        })}
      </div>
      <button className="bp" onClick={runCompare} disabled={comparing||selected.length<2}>
        {comparing?t("Se compară..."):`⚖ ${t("Compară")} ${selected.length} ${t("studii cu AI")}`}
      </button>
      {comparing&&<HeartSpinner label={t("Se compară studiile imagistice...")}/>}
      {result&&!comparing&&(
        <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
          {result.generatedAt&&<div style={{fontSize:10,color:"var(--text-4)",textAlign:"right"}}>{t("Comparație salvată")} · {result.comparedDates?.join(" vs ")} · {result.generatedAt}</div>}
          {result.evolution&&<div className="card" style={{border:"1px solid var(--blue-line)",margin:0}}><div className="lbl" style={{color:"var(--blue)"}}>{t("Evoluție")}</div><div style={{fontSize:13,lineHeight:1.6,marginTop:4}}>{toText(result.evolution)}</div></div>}
          {asArr(result.improvements).length>0&&<div className="card" style={{margin:0}}><div className="lbl" style={{color:"var(--green)"}}>{t("Ameliorări")}</div>{asArr(result.improvements).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:4}}>✓ {toText(x)}</div>)}</div>}
          {asArr(result.worsenings).length>0&&<div className="card" style={{margin:0,border:"1px solid var(--red-line)"}}><div className="lbl" style={{color:"var(--red)"}}>{t("Agravări")}</div>{asArr(result.worsenings).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:4}}>⚠ {toText(x)}</div>)}</div>}
          {asArr(result.stable).length>0&&<div className="card" style={{margin:0}}><div className="lbl" style={{color:"var(--text-2)"}}>{t("Stabil")}</div>{asArr(result.stable).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:4}}>— {toText(x)}</div>)}</div>}
          {result.notes&&<div style={{fontSize:11,color:"var(--text-3)",fontStyle:"italic"}}>{toText(result.notes)}</div>}
          {result.recommendation&&<div className="card" style={{margin:0,border:"1px solid #2d4a1e"}}><div className="lbl" style={{color:"#a3e635"}}>{t("Recomandare")}</div><div style={{fontSize:12,color:"var(--text-2)",marginTop:4}}>{toText(result.recommendation)}</div></div>}
          <div style={{fontSize:10,color:"var(--text-4)",textAlign:"center"}}>{t("⚠️ Comparație generată de AI — nu înlocuiește interpretarea medicului radiolog.")}</div>
        </div>
      )}
    </div>
  );
}

// Lista completă de analize pentru dropdown

export function AddAnalysisManualForm({customAnalytes, onAddCustomAnalyte, onDeleteCustomAnalyte, onSave, initialName}) {
  const allOptions = [...ALL_ANALYTES.map(a=>a.name), ...(customAnalytes||[])];
  // Când vine dintr-o analiză deschisă, o pre-selectăm (nume + unitate + interval)
  const found0 = initialName ? ALL_ANALYTES.find(a=>a.name===initialName) : null;
  const [q, setQ] = React.useState(initialName||"");
  const [selected, setSelected] = React.useState(initialName||null); // {name, unit, min, max}
  const [customName] = React.useState("");
  const [date, setDate] = React.useState(todayISO());
  const [value, setValue] = React.useState("");
  const [unit, setUnit] = React.useState(found0?.unit||"");
  const [refMin, setRefMin] = React.useState(found0?.min!=null?String(found0.min):"");
  const [refMax, setRefMax] = React.useState(found0?.max!=null?String(found0.max):"");
  const [showDropdown, setShowDropdown] = React.useState(false);

  const filtered = q.length>0 ? allOptions.filter(n=>foldDia(n).includes(foldDia(q))) : allOptions;
  const isCustom = selected==="__custom__";

  function selectAnalyte(name) {
    const found = ALL_ANALYTES.find(a=>a.name===name);
    setSelected(name);
    setQ(name);
    setUnit(found?.unit||"");
    setRefMin(found?.min!=null?String(found.min):"");
    setRefMax(found?.max!=null?String(found.max):"");
    setShowDropdown(false);
  }

  function computeStatus() {
    const n=parseFloat(String(value).replace(",","."));
    const lo=parseFloat(refMin); const hi=parseFloat(refMax);
    if(isNaN(n)) return "normal";
    if(!isNaN(hi)&&n>hi) return "high";
    if(!isNaN(lo)&&n<lo) return "low";
    return "normal";
  }

  const finalName = isCustom ? customName : (selected||q);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Selector analiză */}
      <div style={{position:"relative"}}>
        <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Denumire analiză")}</div>
        <input value={q} onChange={e=>{setQ(e.target.value);setSelected(null);setShowDropdown(true);}}
          onFocus={()=>setShowDropdown(true)}
          style={{width:"100%",boxSizing:"border-box"}} placeholder={t("Caută sau scrie denumirea...")}/>
        {showDropdown&&filtered.length>0&&(
          <div style={{position:"absolute",zIndex:100,background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:8,maxHeight:280,overflowY:"auto",width:"100%",top:"100%",marginTop:2}}>
            {(()=>{
              // Grupează pe categorii
              const byCat = {};
              filtered.forEach(name=>{
                const found = ALL_ANALYTES.find(a=>a.name===name);
                const cat = found?.cat || "Personalizate";
                if(!byCat[cat]) byCat[cat]=[];
                byCat[cat].push(name);
              });
              // Personalizate primele
              const cats = Object.keys(byCat).sort((a,b)=>a==="Personalizate"?-1:b==="Personalizate"?1:0);
              return cats.map(cat=>(
                <div key={cat}>
                  <div style={{padding:"6px 12px",fontSize:10,fontWeight:700,color:cat==="Personalizate"?"var(--amber)":"var(--blue)",background:"var(--bg)",letterSpacing:".05em",position:"sticky",top:0}}>{t(cat)}</div>
                  {byCat[cat].map(name=>(
                    <div key={name} style={{display:"flex",alignItems:"center",borderBottom:"1px solid var(--border)"}}>
                      <div onClick={()=>selectAnalyte(name)}
                        style={{flex:1,padding:"8px 12px",cursor:"pointer",fontSize:13}}
                        onMouseDown={e=>e.preventDefault()}>
                        {name}
                      </div>
                      {cat==="Personalizate"&&onDeleteCustomAnalyte&&(
                        <button onMouseDown={e=>{e.preventDefault();e.stopPropagation();fmConfirm(`${t("Ștergi analiza personalizată")} "${name}"?`,{danger:true,okText:"Șterge"}).then(ok=>{if(ok)onDeleteCustomAnalyte(name);});}}
                          style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",padding:"6px 12px",fontSize:14}} title={t("Șterge")}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              ));
            })()}
            {q.length>2&&!allOptions.includes(q)&&(
              <div onClick={()=>{onAddCustomAnalyte&&onAddCustomAnalyte(q);selectAnalyte(q);}}
                style={{padding:"8px 12px",cursor:"pointer",fontSize:12,color:"var(--blue)",borderTop:"1px solid var(--border-2)"}}
                onMouseDown={e=>e.preventDefault()}>
                + {t("Adaugă")} "{q}" {t("ca analiză nouă")}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Data")}</div>
        <DateField value={date} onChange={v=>setDate(v)}/>
      </div>

      <div style={{display:"flex",gap:10}}>
        <div style={{flex:2}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Valoare")}</div>
          <input value={value} onChange={e=>setValue(e.target.value)} type="number" step="any"
            style={{width:"100%",boxSizing:"border-box"}} placeholder={t("ex: 5.2")}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Unitate")}</div>
          <input value={unit} onChange={e=>setUnit(e.target.value)}
            style={{width:"100%",boxSizing:"border-box"}} placeholder={t("ex: g/dL")}/>
        </div>
      </div>

      <div style={{display:"flex",gap:10}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Minim referință")}</div>
          <input value={refMin} onChange={e=>setRefMin(e.target.value)} type="number" step="any"
            style={{width:"100%",boxSizing:"border-box"}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Maxim referință")}</div>
          <input value={refMax} onChange={e=>setRefMax(e.target.value)} type="number" step="any"
            style={{width:"100%",boxSizing:"border-box"}}/>
        </div>
      </div>

      {value&&(
        <div style={{fontSize:12,color:"var(--text-3)"}}>
          {t("Status calculat:")} <span style={{color:computeStatus()==="normal"?"var(--green)":computeStatus()==="high"?"var(--red)":"var(--amber)",fontWeight:600}}>
            {computeStatus()==="normal"?t("Normal"):computeStatus()==="high"?t("↑ Crescut"):t("↓ Scăzut")}
          </span>
        </div>
      )}

      <button className="bp" style={{width:"100%",marginTop:4}} onClick={()=>{
        if(!finalName||!value||!date) return fmAlert(t("Completează denumirea, valoarea și data."));
        onSave({
          name:finalName, date, value, unit,
          referenceRange: refMin&&refMax?refMin+"-"+refMax:"",
          referenceMin:refMin, referenceMax:refMax,
          status:computeStatus()
        });
      }}>{t("Salvează")}</button>
    </div>
  );
}

// Extrage min/max dintr-un interval de referință text ("3.9-6.1", "< 50", "≥ 12"...)
function parseRefRange(txt){
  const s = String(txt||"").replace(",",".").replace(/\s/g,"");
  let m;
  if((m = s.match(/^(-?\d+(?:\.\d+)?)[-–÷…]+(-?\d+(?:\.\d+)?)$/))) return {min:m[1], max:m[2]};
  if((m = s.match(/^[<≤](-?\d+(?:\.\d+)?)$/))) return {min:"", max:m[1]};
  if((m = s.match(/^[>≥](-?\d+(?:\.\d+)?)$/))) return {min:m[1], max:""};
  return {min:"", max:""};
}

export function EditAnalysisValueForm({initial, selAnalyte, onSave}) {
  // Dacă min/max lipsesc dar există interval text, îl despărțim automat în căsuțe
  const seededRange = (initial?.referenceMin||initial?.referenceMax) ? {min:initial?.referenceMin||"", max:initial?.referenceMax||""} : parseRefRange(initial?.referenceRange);
  const [val, setVal] = React.useState({
    name: initial?.name||selAnalyte||"",
    date: initial?.date||"",
    value: initial?.value||"",
    unit: initial?.unit||"",
    referenceRange: initial?.referenceRange||"",
    referenceMin: seededRange.min,
    referenceMax: seededRange.max,
    status: initial?.status||"normal",
  });
  const nameOptions = React.useMemo(()=>{
    const cur = initial?.name||selAnalyte||"";
    const names = ALL_ANALYTES.map(a=>a.name);
    return cur && !names.includes(cur) ? [cur, ...names] : names;
  }, [initial, selAnalyte]);
  function computeStatus(v, min, max) {
    const n = parseFloat(String(v).replace(",","."));
    const lo = parseFloat(min); const hi = parseFloat(max);
    if(isNaN(n)) return "normal";
    if(!isNaN(hi) && n > hi) return "high";
    if(!isNaN(lo) && n < lo) return "low";
    return "normal";
  }
  function handleChange(field, value) {
    setVal(prev=>{
      const next = {...prev, [field]: value};
      // Auto-recalculate status when value or limits change
      if(["value","referenceMin","referenceMax"].includes(field)) {
        next.status = computeStatus(next.value, next.referenceMin, next.referenceMax);
      }
      return next;
    });
  }
  const STATUS_COLORS = {normal:"var(--green)", high:"var(--red)", low:"var(--amber)"};
  const STATUS_LABELS = {normal:"Normal", high:"↑ Crescut", low:"↓ Scăzut"};
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:13,color:"var(--text-2)",marginBottom:4}}>{t("Modifică valorile extrase din document.")}</div>
      <div>
        <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Denumire analiză")}</div>
        <SearchableSelect options={nameOptions} value={val.name} onChange={v=>handleChange("name",v)} placeholder={t("Caută denumirea...")}/>
        <div style={{fontSize:10,color:"var(--text-4)",marginTop:3}}>{t("Alege din listă — redenumirea la o analiză existentă îi alătură valorile la istoric.")}</div>
      </div>
      <div>
        <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Data analizei")}</div>
        <DateField value={val.date} onChange={v=>handleChange("date",v)}/>
      </div>
      <div style={{display:"flex",gap:10}}>
        <div style={{flex:2}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Valoare")}</div>
          <input value={val.value} onChange={e=>handleChange("value",e.target.value)}
            style={{width:"100%",boxSizing:"border-box"}} placeholder={t("ex: 5.2")}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Unitate")}</div>
          <input value={val.unit} onChange={e=>handleChange("unit",e.target.value)}
            style={{width:"100%",boxSizing:"border-box"}} placeholder={t("ex: mmol/L")}/>
        </div>
      </div>
      <div style={{display:"flex",gap:10}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Minim referință")}</div>
          <input value={val.referenceMin} onChange={e=>handleChange("referenceMin",e.target.value)}
            style={{width:"100%",boxSizing:"border-box"}} placeholder={t("ex: 3.9")} type="number" step="any"/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("Maxim referință")}</div>
          <input value={val.referenceMax} onChange={e=>handleChange("referenceMax",e.target.value)}
            style={{width:"100%",boxSizing:"border-box"}} placeholder={t("ex: 6.1")} type="number" step="any"/>
        </div>
      </div>
      <div>
        <div style={{fontSize:11,color:"var(--text-3)",marginBottom:6}}>{t("Status")}</div>
        <span className="pill" style={{display:"inline-block",padding:"5px 14px",fontWeight:700,fontSize:13,
          background:val.status==="normal"?"var(--green-bg-soft)":val.status==="high"?"var(--red-bg-soft)":"var(--amber-bg)",
          color:STATUS_COLORS[val.status]}}>{t(STATUS_LABELS[val.status])}</span>
      </div>
      <button className="bp" onClick={()=>{
        // Reconstruiește intervalul-text din min/max (pentru afișare/PDF)
        const mn=val.referenceMin, mx=val.referenceMax;
        const rr = (mn&&mx) ? `${mn}-${mx}` : mx ? `< ${mx}` : mn ? `> ${mn}` : val.referenceRange;
        onSave({...val, referenceRange: rr});
      }} style={{marginTop:4}}>{t("✓ Salvează modificările")}</button>
    </div>
  );
}

export function LabList({values,onSelect}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {values.map((v,i)=>(
        <div key={i} onClick={()=>onSelect(v.name)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:v.status==="normal"?"var(--surface)":v.status==="high"?"var(--red-bg-soft)":"var(--amber-bg-soft)",border:`1px solid ${v.status==="normal"?"var(--border)":v.status==="high"?"var(--red-bg)":"var(--amber-bg)"}`,borderRadius:10,cursor:"pointer",transition:"all .15s"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,wordBreak:"break-word"}}>{v.name}</div>
            <div style={{fontSize:11,color:"var(--text-3)",whiteSpace:"normal",overflowWrap:"anywhere",wordBreak:"break-word",lineHeight:1.4}}>{v.referenceRange||"—"} {v.unit}</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:17,fontWeight:700,color:v.status==="normal"?"var(--green)":v.status==="high"?"var(--red)":"var(--amber)"}}>{v.value} <span style={{fontSize:11,fontWeight:400,color:"var(--text-3)"}}>{v.unit}</span></div>
            <span className="pill" style={{background:v.status==="normal"?"var(--green-bg-soft)":v.status==="high"?"var(--red-bg-soft)":"var(--amber-bg)",color:v.status==="normal"?"var(--green)":v.status==="high"?"var(--red)":"var(--amber)",fontSize:9}}>{v.status==="normal"?t("Normal"):v.status==="high"?t("↑ Crescut"):t("↓ Scăzut")}</span>
          </div>
          {v.histCount>1&&<div style={{fontSize:11,color:"var(--text-4)",flexShrink:0}}>{v.histCount}×</div>}
          <div style={{color:"var(--border-2)",fontSize:14}}>›</div>
        </div>
      ))}
    </div>
  );
}

export function MetricChart({activeM, MCFG, chartDataByType, metrics}) {
  const allTypeData = chartDataByType?.[activeM]||[];
  // Grafic scrollabil orizontal: toate valorile, lățime proporțională, vizibile ultimele ~15
  const PT_WIDTH = 46; // px per punct
  const chartWidth = Math.max(allTypeData.length * PT_WIDTH, 300);
  const scrollRef = React.useRef(null);
  // La montare / schimbare tip: scroll la dreapta (cele mai recente)
  React.useEffect(()=>{
    if(scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
  }, [activeM, allTypeData.length]);

  return (
    <div className="card" style={{padding:"16px 8px 8px",marginBottom:14}}>
      <div style={{fontSize:12,color:"var(--text-3)",marginBottom:8,paddingLeft:8,fontWeight:600}}>{t(MCFG[activeM].label)} ({MCFG[activeM].unit}) · {allTypeData.length} {t("înregistrări")}</div>
      <div ref={scrollRef} style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        <div style={{width:chartWidth,minWidth:"100%"}}>
          <LineChart width={chartWidth} height={activeM==="tensiune"?220:200} data={allTypeData} margin={{bottom: activeM==="tensiune"?20:0, right:12}}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
            <XAxis dataKey="date" tick={{fontSize:10,fill:"var(--text-3)"}}/>
            <YAxis tick={{fontSize:10,fill:"var(--text-3)"}} width={40}/>
            <Tooltip contentStyle={{background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:8,fontSize:12}} labelStyle={{color:"var(--text)"}}/>
            {activeM==="tensiune"&&<Legend wrapperStyle={{fontSize:11,paddingTop:12}} formatter={v=>t(v)}/>}
            {MCFG[activeM].keys.map(k=><Line key={k.k} type="monotone" dataKey={k.k} name={t(k.k)} stroke={k.c} strokeWidth={2} dot={{r:4,fill:k.c}} activeDot={{r:6}} connectNulls={false}/>)}
          </LineChart>
        </div>
      </div>

    </div>
  );
}

export function AnalizeTab({analysisDocs,metrics,setMetrics,setModal,chartData,chartDataByType,family,onEditMetric,onDeleteMetric,savedMetricOrder,onSaveMetricOrder,onUpdateAnalysisValue,onClearExtraction,callAI,readOnly=false,onGoSettings}) {
  const [view,setView] = useState("latest");
  const [selAnalyte,setSelAnalyte] = useState(null);
  const [groupCat,setGroupCat] = useState(false);
  const [showManualEntries,setShowManualEntries] = useState(false);
  const [allView,setAllView] = useState("values"); // "values" | "chart" pentru Toate analizele
  const [dragMetric,setDragMetric] = useState(null);
  const [dragOverMetric,setDragOverMetric] = useState(null);
  const [activeM,setActiveM] = useState("tensiune");
  const validOrder = (arr)=>{ const base=["tensiune","glicemie","greutate","saturatie","puls","temperatura"]; if(!Array.isArray(arr))return base; const f=arr.filter(x=>base.includes(x)); base.forEach(x=>{if(!f.includes(x))f.push(x);}); return f; };
  const [metricOrder,setMetricOrder] = useState(()=>validOrder(savedMetricOrder));
  function reorder(newOrder){ setMetricOrder(newOrder); if(onSaveMetricOrder) onSaveMetricOrder(newOrder); }
  // Primul din ordine devine cel activ (implicit)
  React.useEffect(()=>{ setActiveM(metricOrder[0]); }, [metricOrder]);
  const MCFG = {
    tensiune:{keys:[{k:"Sistolică",c:"var(--blue)"},{k:"Diastolică",c:"#f97316"}],unit:"mmHg",label:"Tensiune"},
    glicemie:{keys:[{k:"Glicemie",c:"var(--amber)"}],unit:"mmol/L",label:"Glicemie"},
    greutate:{keys:[{k:"Greutate",c:"var(--green)"}],unit:"kg",label:"Greutate"},
    saturatie:{keys:[{k:"Saturație O₂",c:"var(--purple)"}],unit:"%",label:"Saturație O₂"},
    puls:{keys:[{k:"Puls",c:"var(--red)"}],unit:"bpm",label:"Puls"},
    temperatura:{keys:[{k:"Temperatură",c:"var(--amber-2)"}],unit:"°C",label:"Temperatură"},
  };
  // Normalizare nume analize — evită dublurile din diferențe de scriere,
  // DAR păstrează distincția dată de UNITATE (ex: Neutrofile % vs Neutrofile mii/µL sunt DIFERITE)
  const normalizeAnalyte = (name)=>{
    if(!name) return "";
    let n = String(name).trim()
      .replace(/-/g," ") // cratimele nu trebuie s\u0103 despart\u0103 variante ("Gama - GT" == "Gama-GT")
      .replace(/\s+/g," ").trim().toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g,""); // scoate diacriticele
    // Curăță abrevierile redundante în paranteze DOAR dacă sunt sinonim evident (Hb, Hct...)
    // dar păstrează restul textului
    n = n.replace(/\s*\{[^}]*\}\s*/g," ").replace(/\s+/g," ").trim(); // {Hb} tip acolade = abreviere
    // Scoate o eventuală abreviere finală între paranteze înainte de căutarea în ALIASES —
    // altfel "ALT (TGP)" (din lista manuală) și "ALT" (extras din document) ar da chei diferite
    n = n.replace(/\s*\([^)]*\)\s*$/,"").replace(/\s+/g," ").trim();
    const ALIASES = {
      "glucoza serica":"glicemie","glucoza":"glicemie","glucoza sanguina":"glicemie","glicemia":"glicemie","glucoza plasmatica":"glicemie",
      "hemoglobina glicata":"hba1c","hemoglobina glicozilata":"hba1c","hemoglobina glicozilata a1c":"hba1c","hba1c hemoglobina glicata":"hba1c","glicohemoglobina":"hba1c",
      "tgp":"alt","alanin aminotransferaza":"alt","alat":"alt","alaninaminotransferaza":"alt","alaninaminotransferaza alt":"alt","gpt":"alt",
      "tgo":"ast","aspartat aminotransferaza":"ast","asat":"ast","aspartataminotransferaza":"ast","got":"ast",
      "gama gt":"ggt","gama glutamil transferaza":"ggt","gama glutamiltransferaza":"ggt","gamma gt":"ggt","gamaglutamiltransferaza":"ggt","gama glutamiltranspeptidaza":"ggt","ggtp":"ggt",
      "colesterol":"colesterol total","colesterolul total":"colesterol total","colesterol seric total":"colesterol total","colesterol seric":"colesterol total",
      "ldl":"colesterol ldl","ldl colesterol":"colesterol ldl","colesterol ldl direct":"colesterol ldl",
      "hdl":"colesterol hdl","hdl colesterol":"colesterol hdl",
      "wbc":"leucocite","globule albe":"leucocite","numar leucocite":"leucocite","leucocite wbc":"leucocite",
      "rbc":"eritrocite","globule rosii":"eritrocite","hematii":"eritrocite","numar eritrocite":"eritrocite",
      "hgb":"hemoglobina","hb":"hemoglobina","hemoglobina hgb":"hemoglobina",
      "hct":"hematocrit","ht":"hematocrit",
      "plt":"trombocite","numar trombocite":"trombocite","plachete":"trombocite",
      "creatinina serica":"creatinina","creatinina sanguina":"creatinina","creatinina plasmatica":"creatinina",
      "uree serica":"uree","uree sanguina":"uree",
      // Azot ureic sanguin (BUN) e o analiză DISTINCTĂ de Uree (măsoară doar azotul din uree)
      "bun":"azot ureic sanguin","azot ureic":"azot ureic sanguin","azot ureic sanguin bun":"azot ureic sanguin","blood urea nitrogen":"azot ureic sanguin","uree azot":"azot ureic sanguin",
      "sideremie":"fier seric","fier":"fier seric","fe":"fier seric","fier seric fe":"fier seric",
      "proteina c reactiva":"pcr","crp":"pcr","pcr cantitativ":"pcr",
      "vsh":"vsh","viteza de sedimentare":"vsh","viteza sedimentare hematii":"vsh","esr":"vsh",
      "acid uric seric":"acid uric","uricemie":"acid uric",
      "calciu seric":"calciu","calcemie":"calciu","calciu total":"calciu",
      "magneziu seric":"magneziu","magnesiu":"magneziu","magneziemie":"magneziu","mg":"magneziu",
      "sodiu seric":"sodiu","natremie":"sodiu","na":"sodiu",
      "potasiu seric":"potasiu","potasemie":"potasiu","kaliemie":"potasiu","k":"potasiu",
      "tsh ultrasensibil":"tsh","tsh reflex":"tsh","hormon tireostimulator":"tsh",
      "ft4":"ft4","t4 liber":"ft4","tiroxina libera":"ft4",
      "ft3":"ft3","t3 liber":"ft3","triiodotironina libera":"ft3",
      "trigliceride serice":"trigliceride","tg":"trigliceride",
      "bilirubina totala":"bilirubina totala","bilirubina t":"bilirubina totala",
      "fosfataza alcalina":"fosfataza alcalina","fal":"fosfataza alcalina","alp":"fosfataza alcalina",
      "vitamina d3":"vitamina d","25 oh vitamina d":"vitamina d","25 hidroxivitamina d":"vitamina d","25 hidroxi vitamina d":"vitamina d","vitamina d 25 oh":"vitamina d",
      "vitamina b12":"vitamina b12","cobalamina":"vitamina b12",
      "acid folic":"acid folic (vitamina b9)","folati":"acid folic (vitamina b9)","vitamina b9":"acid folic (vitamina b9)",
      "feritina serica":"feritina",
      // Plural/singular — aceleași analize apar uneori la singular în extrageri
      "eozinofil":"eozinofile","neutrofil":"neutrofile","limfocit":"limfocite","monocit":"monocite","bazofil":"bazofile","reticulocit":"reticulocite",
      // Variante de scriere pentru numărul absolut (NU se unesc cu procentul — sunt analize separate)
      "eozinofile nr":"eozinofile absolute","neutrofile nr":"neutrofile absolute","limfocite nr":"limfocite absolute","monocite nr":"monocite absolute","bazofile nr":"bazofile absolute",
      // Abrevieri/denumiri alternative românești pentru indicii eritrocitari și plachetari
      "vem":"volum eritrocitar mediu","hem":"hemoglobina eritrocitara medie","chem":"concentratie medie de hemoglobina eritrocitara",
      // MCHC — variante de exprimare (toate = aceeași analiză)
      "mchc":"concentratie medie de hemoglobina eritrocitara","concentratia medie a hemoglobinei eritrocitare":"concentratie medie de hemoglobina eritrocitara","concentratie medie hemoglobina eritrocitara":"concentratie medie de hemoglobina eritrocitara","concentratia medie de hemoglobina eritrocitara":"concentratie medie de hemoglobina eritrocitara","concentratie medie a hemoglobinei eritrocitare":"concentratie medie de hemoglobina eritrocitara","concentratia hemoglobinei eritrocitare medie":"concentratie medie de hemoglobina eritrocitara",
      // MCH — variante
      "mch":"hemoglobina eritrocitara medie","hemoglobina eritrocitara medie mch":"hemoglobina eritrocitara medie","continut mediu de hemoglobina":"hemoglobina eritrocitara medie",
      "volum trombocitar mediu":"volum plachetar mediu","vpm":"volum plachetar mediu",
      "largimea distributiei eritrocitare":"indice de distributie a eritrocitelor",
      // Coagulare — denumiri alternative frecvente
      "timp quick":"timp de protrombina","quick":"timp de protrombina","timp de protrombina tp":"timp de protrombina",
      "indice de protrombina":"activitate de protrombina","activitate protrombinica":"activitate de protrombina","ap":"activitate de protrombina",
      // Biochimie
      "globuline":"globuline serice","proteine globuline":"globuline serice",
    };
    if(ALIASES[n]) n = ALIASES[n];
    // Cheia include unitatea normalizată — diferențiază % de mii/µL etc.
    return n;
  };
  // Normalizează unitatea — echivalează scrierile diferite ale aceleiași unități
  // (mii/µL = 10³/µL = K/µL = 10⁹/L; mil/µL = 10⁶/µL = 10¹²/L)
  const unitKey = (u)=>{
    let s = String(u||"").toLowerCase().replace(/\s+/g,"").replace(/μ/g,"µ").replace(/\/ul$/,"/µl").trim();
    s = s.replace(/^x/,"");
    if(/^(mii\/µl|10[³3]\/µl|10\^3\/µl|10\*3\/µl|10e3\/µl|k\/µl|10⁹\/l|10\^9\/l|109\/l)$/.test(s)) return "10³/µl";
    if(/^(mil\/µl|milioane\/µl|10[⁶6]\/µl|10\^6\/µl|10\*6\/µl|10e6\/µl|m\/µl|10¹²\/l|10\^12\/l|1012\/l)$/.test(s)) return "10⁶/µl";
    return s;
  };
  // Analizele de tip formulă leucocitară vin în DOUĂ variante — procent și număr absolut —
  // care sunt analize DIFERITE: dacă unitatea e "mii/µL", numele primește sufixul "absolute"
  const DIFF_CELLS = ["neutrofile","limfocite","monocite","eozinofile","bazofile","reticulocite"];
  const adjustNormForUnit = (norm, uk)=> (DIFF_CELLS.includes(norm) && uk==="10³/µl") ? norm+" absolute" : norm;
  // Nume oficiale — aceleași ca la adăugarea manuală (ALL_ANALYTES), ca extragerea din
  // documente și adăugarea manuală să afișeze mereu aceeași denumire pentru aceeași analiză.
  // Fiecare intrare se înregistrează și sub abrevierea din paranteze ("Alanin-aminotransferază (ALT)"
  // răspunde și la "alt"), fiindcă în buletine apare adesea doar abrevierea.
  const canonicalByKey = {};
  ALL_ANALYTES.forEach(a=>{
    const uk = unitKey(a.unit);
    const kFull = adjustNormForUnit(normalizeAnalyte(a.name), uk);
    if(kFull && !canonicalByKey[kFull]) canonicalByKey[kFull] = a.name;
    const paren = String(a.name).match(/\(([^)]+)\)\s*$/);
    if(paren){
      const kAbbr = adjustNormForUnit(normalizeAnalyte(paren[1]), uk);
      if(kAbbr && !canonicalByKey[kAbbr]) canonicalByKey[kAbbr] = a.name;
    }
  });
  const analyteMap = {};
  const nameUnit = {}; // nume afișat -> unitatea (normalizată) primei apariții
  analysisDocs.forEach(ad=>{(ad.values||[]).forEach((v,vi)=>{
    const norm0 = normalizeAnalyte(v.name);
    if(!norm0) return;
    const uk = unitKey(v.unit);
    const norm = adjustNormForUnit(norm0, uk);
    let display;
    if(canonicalByKey[norm]) {
      display = canonicalByKey[norm];
    } else {
      display = String(v.name).replace(/\s*\{[^}]*\}\s*/g," ").replace(/\s+/g," ").trim();
      // Unitatea se afișează separat, lângă valoare — o scoatem din nume dacă apare deja inclusă acolo
      // (ex: numele extras e "Eozinofile (%)" iar unitatea e tot "%" -> ar apărea dublat)
      if(v.unit) {
        const escUnit = String(v.unit).replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
        display = display.replace(new RegExp(`\\s*\\(\\s*${escUnit}\\s*\\)\\s*$`,'i'),'').trim();
      }
    }
    // Aceeași analiză raportată în ALTĂ unitate = serie separată (valorile nu-s comparabile
    // între ele pe același grafic) — diferențiem numele prin unitate, ca ultimă soluție
    if(nameUnit[display]!==undefined && nameUnit[display]!==uk) display = `${display} (${v.unit||t("altă unitate")})`;
    if(nameUnit[display]===undefined) nameUnit[display]=uk;
    if(!analyteMap[display])analyteMap[display]=[];
    analyteMap[display].push({...v,date:ad.date,docName:ad.docName,docId:ad.docId,valueIndex:vi});
  });});
  Object.values(analyteMap).forEach(arr=>arr.sort((a,b)=>b.date.localeCompare(a.date)));
  const latest = Object.entries(analyteMap).map(([name,hist])=>({...hist[0],name,histCount:hist.length}));
  const grouped = {};
  latest.forEach(v=>{const c=getCat(v.name);if(!grouped[c])grouped[c]=[];grouped[c].push(v);});

  if(view==="history"&&selAnalyte) {
    const hist=analyteMap[selAnalyte]||[];
    const chartH=[...hist].reverse().map(h=>({date:fmtD(h.date),Valoare:parseFloat(h.value)||0,_status:h.status}));
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          <button className="bg" onClick={()=>{setView("latest");setSelAnalyte(null);}}>{t("← Înapoi")}</button>
          {!readOnly&&<button className="bg" style={{color:"var(--blue)",borderColor:"var(--blue)"}} onClick={()=>setModal({type:"addAnalysisManual",data:{analysisDocs,onUpdateAnalysisValue,analyteName:selAnalyte}})}>{t("+ Adaugă valoare")}</button>}
        </div>
        <div style={{fontWeight:700,fontSize:20,marginBottom:2}}>{selAnalyte}</div>
        <div style={{fontSize:13,color:"var(--text-3)",marginBottom:14}}>{hist[0]?.unit} · {hist.length} {t("înregistrări")}</div>
        {chartH.length>1&&(
          <div className="card" style={{padding:"16px 8px 8px",marginBottom:14}}>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartH}>
                <defs>
                  <linearGradient id="histGrad" x1="0" y1="0" x2="1" y2="0">
                    {segmentStops(chartH).map((s,i)=><stop key={i} offset={`${s.o}%`} stopColor={s.c}/>)}
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                <XAxis dataKey="date" tick={{fontSize:10,fill:"var(--text-3)"}}/>
                <YAxis tick={{fontSize:10,fill:"var(--text-3)"}} width={40}/>
                <Tooltip contentStyle={{background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:8,fontSize:12}}/>
                <Line type="monotone" dataKey="Valoare" stroke="url(#histGrad)" strokeWidth={2.5} dot={<StatusDot r={4}/>} activeDot={{r:6}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="card" style={{padding:0,overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:520}}>
            <thead><tr style={{background:"var(--bg)"}}>
              {["Data","Valoare","Interval","Status","Document",""].map(h=>(
                <th key={h} style={{padding:"9px 12px",textAlign:"left",color:"var(--text-3)",fontSize:11.5,fontWeight:600}}>{t(h)}</th>
              ))}
            </tr></thead>
            <tbody>
              {hist.map((h,i)=>(
                <tr key={i} style={{borderTop:"1px solid var(--border)",background:h.status!=="normal"?"rgba(248,113,113,.04)":"transparent"}}>
                  <td style={{padding:"9px 12px",fontFamily:"monospace",fontSize:12,color:"var(--text-2)"}}>{fmtD(h.date)}</td>
                  <td style={{padding:"9px 12px",fontWeight:700,color:h.status==="normal"?"var(--green)":h.status==="high"?"var(--red)":"var(--amber)"}}>{h.value} <span style={{fontSize:11,color:"var(--text-3)",fontWeight:400}}>{h.unit}</span></td>
                  <td style={{padding:"9px 12px",fontFamily:"monospace",fontSize:10,color:"var(--text-4)",maxWidth:110,whiteSpace:"normal",wordBreak:"break-all",overflowWrap:"anywhere"}}>{h.referenceRange||"—"}</td>
                  <td style={{padding:"9px 12px"}}><span className="pill" style={{background:h.status==="normal"?"var(--green-bg-soft)":h.status==="high"?"var(--red-bg-soft)":"var(--amber-bg)",color:h.status==="normal"?"var(--green)":h.status==="high"?"var(--red)":"var(--amber)",fontSize:10}}>{h.status==="normal"?t("Normal"):h.status==="high"?t("↑ Crescut"):t("↓ Scăzut")}</span></td>
                  <td style={{padding:"9px 12px",fontSize:11,color:"var(--text-3)",maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.docName}</td>
                  <td style={{padding:"9px 6px",whiteSpace:"nowrap"}}>
                    {!readOnly&&<button title={t("Editează valoarea")} onClick={()=>setModal({type:"editAnalysisValue",data:{h,selAnalyte,analysisDocs,onUpdateAnalysisValue}})}
                      style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",padding:"3px 5px",fontSize:13}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>}
                    {!readOnly&&<button title={t("Șterge această valoare")} onClick={()=>{
                      fmConfirm(t("Ștergi această valoare din istoric?"),{danger:true,okText:"Șterge"}).then(ok=>{
                        if(!ok) return;
                        const ad = analysisDocs.find(a=>a.docId===h.docId);
                        if(!ad) return;
                        const newValues = (ad.values||[]).filter((_,vi)=>vi!==h.valueIndex);
                        if(onUpdateAnalysisValue) onUpdateAnalysisValue(h.docId, newValues);
                      });
                    }} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",padding:"3px 5px",fontSize:13}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="subtabs">
        {[["latest","📋 "+t("Ultimele analize")],["evolution","📈 "+t("Evoluție")],["devices","📲 "+t("Dispozitive")]].map(([k,l])=>(
          <button key={k} className={`stab${view===k?" act":""}`} onClick={()=>setView(k)}>{l}</button>
        ))}
      </div>
      {view==="latest"&&(
        <div>
          {latest.length===0&&(
            <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"40px 16px"}}>
              <div style={{fontSize:40,marginBottom:8}}>🔬</div>
              <div style={{marginBottom:4,color:"var(--text-2)"}}>{t("Nicio analiză încă.")}</div>
              <div style={{fontSize:12,color:"var(--text-3)",lineHeight:1.6,maxWidth:340,margin:"0 auto 16px"}}>
                {t("Încarcă un document în tab-ul")} <strong>{t("Documente")}</strong> {t("și valorile se extrag automat cu AI, sau adaugă o analiză manual aici.")}
              </div>
              {!readOnly&&<button className="bp" style={{fontSize:13,padding:"9px 18px"}} onClick={()=>setModal({type:"addAnalysisManual",data:{analysisDocs,onUpdateAnalysisValue}})}>{t("+ Adaugă analiză manual")}</button>}
            </div>
          )}
          {latest.length>0&&(
            <div>
              <div style={{marginBottom:12,padding:"9px 12px",background:"var(--amber-bg-soft)",border:"1px solid var(--amber-line)",borderRadius:10,fontSize:11,color:"#c4a355",display:"flex",gap:8,alignItems:"flex-start"}}>
                <span style={{flexShrink:0}}>ⓘ</span>
                <span>{t("Valorile sunt extrase automat de AI și pot conține erori. Verifică-le cu documentul original. Nu înlocuiesc interpretarea unui medic.")}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,gap:6,flexWrap:"wrap"}}>
                <div className="sec" style={{margin:0}}>{t("Cele mai recente valori")}</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button className="bg" style={{fontSize:11,padding:"5px 10px",color:"var(--purple)",borderColor:"var(--purple)"}} onClick={()=>setModal({type:"interpretAnalyses",data:{latest,analyteMap}})}>✨ {t("Interpretare AI")}</button>
                  <button className="bg" style={{fontSize:11,padding:"5px 10px"}} onClick={()=>setGroupCat(g=>!g)}>{groupCat?"📋 "+t("Listă"):"📂 "+t("Categorii")}</button>
                  {!readOnly&&<button className="bg" style={{fontSize:11,padding:"5px 10px",color:"var(--blue)",borderColor:"var(--blue)"}} onClick={()=>setModal({type:"addAnalysisManual",data:{analysisDocs,onUpdateAnalysisValue}})}>{t("+ Adăugare")}</button>}
                </div>
              </div>
              {groupCat
                ?Object.entries(grouped).filter(([,vs])=>vs.length>0).map(([cat,vs])=>(
                    <div key={cat}><div className="sec" style={{marginTop:8,color:"var(--text-2)"}}>{t(cat)}</div><LabList values={vs} onSelect={n=>{setSelAnalyte(n);setView("history");}}/></div>
                  ))
                :<LabList values={latest} onSelect={n=>{setSelAnalyte(n);setView("history");}}/>
              }
            </div>
          )}
        </div>
      )}
      {view==="evolution"&&(
        <div>
          <div style={{marginBottom:12}}>
            <div style={{display:"flex",gap:6,overflowX:"auto",minWidth:0,WebkitOverflowScrolling:"touch",scrollbarWidth:"none",paddingBottom:2}}>
              {metricOrder.map((k,i)=>(
                <button key={k} className={activeM===k?"bp":"bg"}
                  draggable={!readOnly}
                  onDragStart={()=>!readOnly&&setDragMetric(k)}
                  onDragEnd={()=>{setDragMetric(null);setDragOverMetric(null);}}
                  onDragOver={e=>{if(readOnly)return;e.preventDefault();setDragOverMetric(k);}}
                  onDrop={()=>{
                    if(readOnly)return;
                    if(dragMetric&&dragMetric!==k){
                      const n=[...metricOrder];
                      const from=n.indexOf(dragMetric); const to=n.indexOf(k);
                      n.splice(to,0,n.splice(from,1)[0]);
                      reorder(n);
                    }
                    setDragMetric(null);setDragOverMetric(null);
                  }}
                  style={{fontSize:12,padding:"6px 12px",whiteSpace:"nowrap",flexShrink:0,
                    opacity:dragMetric===k?0.4:1,
                    borderLeft:dragOverMetric===k&&dragMetric!==k?"2px solid var(--blue)":undefined}}
                  onClick={()=>setActiveM(k)}>{t(MCFG[k].label)}</button>
              ))}
            </div>
          </div>
          <MetricChart activeM={activeM} MCFG={MCFG} chartDataByType={chartDataByType} metrics={metrics}/>
          {/* Toate înregistrările din Evoluție (manuale + extrase din documente) — editabile/ștergibile */}
          {(()=>{
            const allEntries = [...metrics].sort((a,b)=>b.date.localeCompare(a.date));
            return (
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
                  {allEntries.length>0&&(
                    <button className="bg" style={{fontSize:12,padding:"7px 14px"}} onClick={()=>setShowManualEntries(s=>!s)}>
                      {showManualEntries?"▾":"▸"} {t("Gestionează valorile")} ({allEntries.length})
                    </button>
                  )}
                  {!readOnly&&<button className="bg" style={{fontSize:12,padding:"7px 14px",color:"var(--blue)",borderColor:"var(--blue)"}} onClick={()=>setModal({type:"addMetric"})}>{t("+ Adăugare")}</button>}
                </div>
                {showManualEntries&&(
                <div className="card" style={{padding:0,overflow:"hidden"}}>
                  {allEntries.map((m,i)=>(
                    <div key={m._dbId||i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderTop:i>0?"1px solid var(--border)":"none"}}>
                      <div style={{minWidth:74}}>
                        <div style={{fontFamily:"monospace",fontSize:12,color:"var(--text-2)"}}>{fmtD(m.date)}</div>
                        <div style={{fontSize:9,color:"var(--text-4)"}} title={m.sourceDoc||""}>{m.source==="analize"?"📄 "+t("din document"):"✍️ "+t("manual")}</div>
                      </div>
                      <div style={{flex:1,display:"flex",flexWrap:"wrap",gap:8,fontSize:12}}>
                        {(m.ts_sys!=null||m.ts_dia!=null)&&<span style={{color:"var(--blue)"}}>🩸 {m.ts_sys??"—"}/{m.ts_dia??"—"} <span style={{color:"var(--text-4)",fontSize:10}}>{t("mmHg")}</span></span>}
                        {m.glicemie!=null&&<span style={{color:"var(--amber)"}}>🍬 {m.glicemie} <span style={{color:"var(--text-4)",fontSize:10}}>{t("mmol/L")}</span></span>}
                        {m.greutate!=null&&<span style={{color:"var(--green)"}}>⚖️ {m.greutate} <span style={{color:"var(--text-4)",fontSize:10}}>{t("kg")}</span></span>}
                        {m.saturatie!=null&&<span style={{color:"var(--purple)"}}>🫁 {m.saturatie} <span style={{color:"var(--text-4)",fontSize:10}}>%</span></span>}
                        {m.puls!=null&&<span style={{color:"var(--red)"}}>💓 {m.puls} <span style={{color:"var(--text-4)",fontSize:10}}>{t("bpm")}</span></span>}
                        {m.temperatura!=null&&<span style={{color:"var(--amber-2)"}}>🌡️ {m.temperatura} <span style={{color:"var(--text-4)",fontSize:10}}>°C</span></span>}
                      </div>
                      {!readOnly&&<button onClick={()=>onEditMetric(m)} style={{background:"none",border:"none",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center"}} title={t("Editează")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>}
                      {!readOnly&&<button onClick={()=>fmConfirm(t("Ștergi această înregistrare?"),{danger:true,okText:"Șterge"}).then(ok=>{if(ok)onDeleteMetric(m);})} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center"}} title={t("Șterge")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      </button>}
                    </div>
                  ))}
                </div>
                )}
              </div>
            );
          })()}
          {latest.length>0&&(()=>{
            // Doar datele care au efectiv valori (fără coloane goale, ex. un document fără
            // analize extrase), și TOATE — tabelul e scrollabil orizontal, nu limităm la 5
            const datesWithData=new Set();
            Object.values(analyteMap).forEach(hist=>hist.forEach(h=>{if(h.date)datesWithData.add(h.date);}));
            const allDates=[...datesWithData].sort((a,b)=>b.localeCompare(a));
            // Export Excel al tuturor analizelor
            async function exportExcel() {
              const _xlsx = await import('xlsx'); const XLSX = _xlsx.default||_xlsx;
              const dates=[...new Set(analysisDocs.map(a=>a.date))].sort();
              const names=Object.keys(analyteMap).sort();
              // Rânduri: [Analiză, Unitate, Interval, ...date]
              const header=[t("Analiză"),t("Unitate"),t("Interval referință"),...dates.map(d=>fmtD(d))];
              const rows=names.map(name=>{
                const hist=analyteMap[name]||[];
                const unit=hist[0]?.unit||"";
                const ref=hist[0]?.referenceRange||"";
                return [name,unit,ref,...dates.map(d=>{const e=hist.find(h=>h.date===d);return e?e.value:"";})];
              });
              const ws=XLSX.utils.aoa_to_sheet([header,...rows]);
              // Lățimi coloane
              ws['!cols']=[{wch:32},{wch:10},{wch:16},...dates.map(()=>({wch:12}))];
              const wb=XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb,ws,t("Analize"));
              XLSX.writeFile(wb,"analize_"+todayISO()+".xlsx");
            }
            // Export grafice ca PDF — clonează SVG-urile recharts din DOM
            function exportChartsPDF() {
              const chartCards = document.querySelectorAll('[data-chart-name]');
              if(!chartCards.length){ fmAlert(t("Nu există grafice de exportat. Comută pe vizualizarea Grafic.")); return; }
              let body="";
              chartCards.forEach(card=>{
                const name=card.getAttribute('data-chart-name');
                const svg=card.querySelector('svg');
                if(svg){
                  const clone=svg.cloneNode(true);
                  clone.setAttribute('xmlns','http://www.w3.org/2000/svg');
                  body+=`<div style="page-break-inside:avoid;margin-bottom:24px"><h3 style="margin:0 0 6px;font-size:14px">${escapeHtml(name)}</h3>${clone.outerHTML}</div>`;
                }
              });
              const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t("Grafice analize")}</title>
                <style>body{font-family:Arial,sans-serif;padding:20px;color:#222}h2{margin-bottom:4px}
                .sub{color:#666;font-size:12px;margin-bottom:16px}
                svg{background:#fff}
                @media print{body{padding:0}}</style></head>
                <body><h2>${t("Evoluție analize — grafice")}</h2><div class="sub">${t("Generat:")} ${new Date().toLocaleString("ro-RO")}</div>
                ${body}
                <script>window.onload=()=>window.print();</script></body></html>`;
              const w=window.open("","_blank");
              if(w){ w.document.write(html); w.document.close(); }
              else fmAlert(t("Deblochează pop-up-urile pentru a exporta PDF."));
            }
            // Export PDF — fereastră printabilă
            function exportPDF() {
              const dates=[...new Set(analysisDocs.map(a=>a.date))].sort((a,b)=>b.localeCompare(a));
              const names=Object.keys(analyteMap).sort();
              let rows="";
              names.forEach(name=>{
                const hist=analyteMap[name]||[];
                const unit=hist[0]?.unit||"";
                const cells=dates.map(d=>{
                  const e=hist.find(h=>h.date===d);
                  if(!e) return "<td>—</td>";
                  const color=e.status==="normal"?"#0a7a4a":e.status==="high"?"#c02020":"#b07800";
                  return `<td style="color:${color};font-weight:600">${escapeHtml(e.value)}</td>`;
                }).join("");
                rows+=`<tr><td style="text-align:left;font-weight:600">${escapeHtml(name)}</td><td>${escapeHtml(unit)}</td>${cells}</tr>`;
              });
              const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t("Analize")}</title>
                <style>body{font-family:Arial,sans-serif;padding:20px;color:#222}h2{margin-bottom:4px}
                .sub{color:#666;font-size:12px;margin-bottom:16px}
                table{border-collapse:collapse;width:100%;font-size:11px}
                th,td{border:1px solid #ccc;padding:5px 8px;text-align:center}
                th{background:#f0f0f0}
                @media print{body{padding:0}}</style></head>
                <body><h2>${t("Analize medicale")}</h2><div class="sub">${t("Generat:")} ${new Date().toLocaleString("ro-RO")}</div>
                <table><thead><tr><th style="text-align:left">${t("Analiză")}</th><th>${t("Unitate")}</th>${dates.map(d=>`<th>${fmtD(d)}</th>`).join("")}</tr></thead>
                <tbody>${rows}</tbody></table>
                <script>window.onload=()=>window.print();</script></body></html>`;
              const w=window.open("","_blank");
              if(w){ w.document.write(html); w.document.close(); }
              else fmAlert(t("Deblochează pop-up-urile pentru a exporta PDF."));
            }
            const renderTable=names=>(
              <div className="card" style={{padding:0,overflow:"auto",marginBottom:10}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:380}}>
                  <thead><tr style={{background:"var(--bg)"}}>
                    <th style={{padding:"9px 12px",textAlign:"left",color:"var(--text-3)",fontSize:11,position:"sticky",left:0,background:"var(--bg)",zIndex:1}}></th>
                    {allDates.map(d=><th key={d} style={{padding:"9px 10px",color:"var(--text-3)",fontSize:11,textAlign:"center",whiteSpace:"nowrap"}}>{fmtD(d)}</th>)}
                  </tr></thead>
                  <tbody>
                    {names.map(name=>{
                      const hist=analyteMap[name]||[];
                      return (
                        <tr key={name} style={{borderTop:"1px solid var(--border)",cursor:"pointer"}} onClick={()=>{setSelAnalyte(name);setView("history");}}>
                          <td style={{padding:"8px 12px",fontWeight:600,position:"sticky",left:0,background:"var(--surface)",zIndex:1,fontSize:12,color:"var(--text)"}}>
                            {name}
                          </td>
                          {allDates.map(d=>{
                            const entry=hist.find(h=>h.date===d);
                            return <td key={d} style={{padding:"8px 10px",textAlign:"center",fontFamily:"monospace",fontWeight:entry?700:400,color:!entry?"var(--border-2)":entry.status==="normal"?"var(--green)":entry.status==="high"?"var(--red)":"var(--amber)"}}>{entry?entry.value:"—"}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
            // Vizualizare grafic: mini-grafic pentru fiecare analiză cu istoric >1
            const renderCharts=names=>(
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10,marginBottom:10}}>
                {names.map(name=>{
                  const hist=[...(analyteMap[name]||[])].reverse();
                  if(hist.length<2) return null;
                  const data=hist.map(h=>({date:fmtD(h.date),Valoare:parseFloat(h.value)||0,_status:h.status}));
                  const gid="sg_"+foldDia(name).replace(/[^a-z0-9]/g,"");
                  return (
                    <div key={name} data-chart-name={name} className="card" style={{padding:"12px 6px 4px",cursor:"pointer"}} onClick={()=>{setSelAnalyte(name);setView("history");}}>
                      <div style={{fontSize:12,fontWeight:600,paddingLeft:10,marginBottom:4}}>{name} <span style={{color:"var(--text-4)",fontWeight:400,fontSize:10}}>{hist[hist.length-1]?.unit}</span></div>
                      <ResponsiveContainer width="100%" height={120}>
                        <LineChart data={data}>
                          <defs>
                            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
                              {segmentStops(data).map((s,si)=><stop key={si} offset={`${s.o}%`} stopColor={s.c}/>)}
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{fontSize:8,fill:"var(--text-4)"}}/>
                          <YAxis tick={{fontSize:8,fill:"var(--text-4)"}} width={30}/>
                          <Tooltip contentStyle={{background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:8,fontSize:11}} labelStyle={{color:"var(--text)"}}/>
                          <Line type="monotone" dataKey="Valoare" stroke={`url(#${gid})`} strokeWidth={2} dot={<StatusDot r={3}/>}/>
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })}
              </div>
            );
            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:6,flexWrap:"wrap"}}>
                  <div className="sec" style={{margin:0}}>{t("Toate analizele")}</div>
                  <div style={{display:"flex",gap:6}}>
                    <button className="bg" style={{fontSize:11,padding:"5px 10px"}} onClick={()=>setAllView(v=>v==="values"?"chart":"values")}>{allView==="values"?"📈 "+t("Grafic"):"🔢 "+t("Valori")}</button>
                    <button className="bg" style={{fontSize:11,padding:"5px 10px"}} onClick={()=>setGroupCat(g=>!g)}>{groupCat?"📋 "+t("Listă"):"📂 "+t("Categorii")}</button>
                    {allView==="values"&&<button className="bg" style={{fontSize:11,padding:"5px 10px"}} onClick={exportExcel}>{t("⬇ Excel")}</button>}
                    <button className="bg" style={{fontSize:11,padding:"5px 10px"}} onClick={allView==="chart"?exportChartsPDF:exportPDF}>{t("⬇ PDF")}</button>
                  </div>
                </div>
                {allView==="chart"
                  ?(groupCat
                    ?Object.entries(grouped).filter(([,vs])=>vs.length>0).map(([cat,vs])=>(
                        <div key={cat}><div className="sec" style={{color:"var(--text-2)"}}>{t(cat)}</div>{renderCharts(vs.map(v=>v.name))}</div>
                      ))
                    :renderCharts(latest.map(v=>v.name)))
                  :(groupCat
                    ?Object.entries(grouped).filter(([,vs])=>vs.length>0).map(([cat,vs])=>(
                        <div key={cat}><div className="sec" style={{color:"var(--text-2)"}}>{t(cat)}</div>{renderTable(vs.map(v=>v.name))}</div>
                      ))
                    :renderTable(latest.map(v=>v.name)))
                }
              </div>
            );
          })()}
        </div>
      )}
      {view==="devices"&&(
        <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"40px 16px"}}>
          <div style={{fontSize:40,marginBottom:8}}>📲</div>
          <div style={{marginBottom:4,color:"var(--text-2)"}}>{t("Niciun dispozitiv conectat încă.")}</div>
          <div style={{fontSize:12,color:"var(--text-3)",lineHeight:1.6,maxWidth:340,margin:"0 auto"}}>
            {t("Conectează o aplicație de sănătate din")}{" "}
            <span onClick={onGoSettings} style={{color:"var(--blue)",cursor:"pointer",textDecoration:"underline",fontWeight:600}}>{t("Setări → Aplicații de sănătate")}</span>. {t("Valorile importate vor apărea aici.")}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ TRATAMENT TAB ═══════════════════════════════════════════════════════════
